/**
 * Fulfillment Plan API
 *
 * API functions for managing fulfillment plans.
 * Shared between shipstation-lite and ss-ui-client.
 */

import { httpClient } from './client';

import type { ShippingConfiguration } from '../types/fulfillment-plan';
import type { Address } from '../types/reference-data';

/**
 * Request for saving fulfillment plan configuration
 */
export type SaveConfigRequest = ShippingConfiguration & {
  fulfillmentPlanId: string;
};

type RecipientInfo = {
  name: string | null;
  phone: string | null;
  email: string | null;
};

export type ShipToAndRecipientRequest = {
  fulfillmentPlanIds: string[];
  validateAddress: boolean;
  recipientInfo: RecipientInfo;
  shipTo: Address;
};

/**
 * Saves the ship-to address and recipient info on a fulfillment plan.
 */
export const saveShipToAndRecipient = async (request: ShipToAndRecipientRequest): Promise<void> => {
  await httpClient.put('/fulfillmentplan/shiptoandrecipient', request);
};

/**
 * Save configuration to existing fulfillment plan
 *
 * Updates the shipping configuration for an existing fulfillment plan without
 * calculating rates. Use this endpoint when modifying carrier, service, packages,
 * or other shipping options for orders that already exist.
 *
 * Does NOT save the shipTo address — use `/fulfillmentplan/shiptoandrecipient` for that.
 *
 * @param {number} fulfillmentPlanId - Existing fulfillment plan ID to update
 * @param {SaveConfigRequest} config - Complete shipping configuration to save
 * @example
 * await saveFulfillmentPlanConfig(12345, {
 *   fulfillmentPlanId: '12345',
 *   shipFromId: '123',
 *   shipTo: { name: 'John Doe', line1: '123 Main St', ... },
 *   serviceId: '456',
 *   sellerLabelProviderId: '789',
 *   packages: [{ weight: { value: 16, unit: 'Ounces' }, ... }],
 * });
 */
export const saveFulfillmentPlanConfig = async (fulfillmentPlanId: number, config: SaveConfigRequest): Promise<void> => {
  await httpClient.post(`/fulfillmentplan/saveconfig/${fulfillmentPlanId}`, config);
};
